import React, { useState, useRef, useCallback, useEffect } from 'react';

import {
    Inline,
    Stack,
    DropdownSelect,
    DropdownMenu,
} from '@/shared/ui';
import { useClickOutside } from '@/shared/hooks/useClickOutside';
import styles from '../styles/international-phone-input.css';
import FormFieldContainer from './FormFieldContainer';
import CheckboxField from './CheckboxField';
import { countries, defaultOption, countryCodes } from './phone.constants';
import { INTL_PHONE_INPUT_ID } from '@/hosting/ssr/islands';
import { KeapFormState } from '@/hosting/keap-forms-processing/keap-form-state';
import Hydratable from '@/hosting/ssr/Hydratable';
import { formatPhoneNumber } from './utils';
import { detectCountryCode } from './detectCountryCode';
import { FORM_EVENTS } from '@/hosting/keap-forms-processing/keap-forms.constants';
import { intl, getCompanyName } from '@/shared/intl';
import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';

const options = countries.map((country) => ({
    value: country.code,
    label: `${country.flag} ${country.name}`,
    searchText: `${country.name} ${country.phoneNumberCode}`,
    country,
}));

const countriesByCode = new Map(countries.map((country) => [country.code, country]));

const getSelectedLabel = (countryCode) => {
    const { flag, phoneNumberCode } = countriesByCode.get(countryCode);

    return `${flag} ${phoneNumberCode}`;
};

const renderCountryOption = (option) => (
    <Inline alignX={'spread'} stretch={true}>
        <Inline gap={8}>
            <span> {option.country.flag} </span>

            <span> {option.country.name} </span>
        </Inline>

        <Inline>
            {option.country.phoneNumberCode}
        </Inline>
    </Inline>
);

export function InternationalPhoneInput(props = {}) {
    const [countryCode, setCountryCode] = useState(defaultOption.code);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [e164Value, setE164Value] = useState('');
    const [search, setSearch] = useState('');
    const [previousEmittedValue, setPreviousEmittedValue] = useState('');

    const [showError, setShowError] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [smsOptInChecked, setSmsOptInChecked] = useState(false);

    const e164InputRef = useRef(null);
    const dropdownRef = useRef(null);
    const closeDropdown = useCallback(() => setIsOpen(false), []);

    const isPhoneNumberEmpty = phoneNumber.replace(/\D/g, '').length === 0;
    const isRequired = props.field.required || smsOptInChecked;

    const errorMessage = intl.get('unlayer.tool.form.field.intlPhone.error', {
        defaultMessage: 'Please enter a valid phone number',
    });

    const smsOptinLabel = `By checking this box you agree to receive text messages (including conversational, transactional, and marketing/promotional messages) from ${getCompanyName()}. Consent is not a condition of any purchase, and subscribing is optional and not required. Reply HELP for help or STOP to opt out. Message and data rates may apply. Message frequency varies.`;
    const optInFieldName = props.field.name.replace('value', 'smsOptin');
    const smsOptinProps = {
        field: {
            label: '',
            show_label: false,
            required: false,
            name: optInFieldName,
            type: 'checkbox',
            options: smsOptinLabel,
        },
        error: false,
        className: props.className,
        checkboxIconClassName: styles.smsOptInCheckbox,
        fieldType: 'checkbox',
        checked: smsOptInChecked,
        onValue: (_, checked) => setSmsOptInChecked(checked),
    };
    const showSmsOptInCheckbox = props.field.required_opt_in && !props.field.hidden;

    const setValuesFromE164Number = (value) => {
        if (!value || !isValidPhoneNumber(String(value))) {
            return;
        }

        const parsed = parsePhoneNumberFromString(String(value));

        if (!parsed?.country || !countryCodes.has(parsed.country)) {
            return;
        }

        const parsedCountryCode = parsed.country;

        const { formatted, e164 } = formatPhoneNumber(parsedCountryCode, parsed.nationalNumber);

        setCountryCode(parsedCountryCode);
        setPhoneNumber(formatted);
        setE164Value(e164 ?? String(value));
    };

    useClickOutside(dropdownRef, closeDropdown, isOpen);

    useEffect(() => {
        const detected = detectCountryCode();

        if (detected) {
            setCountryCode(detected);
        }
    }, []);

    useEffect(() => {
        const initialValues = KeapFormState.getInitialFormValues();
        const optInValue = initialValues[optInFieldName];
        const value = initialValues[props.field.name];

        if (Array.isArray(optInValue) && optInValue.length > 0) {
            setSmsOptInChecked(true);
        }

        setValuesFromE164Number(value);
    }, []);

    useEffect(() => {
        const form = e164InputRef.current?.closest('keap-form');

        if (!form) return;

        const onfieldDefaultValuesLoaded = (event) => {
            const { name, defaultValues } = event.detail;
            const value = defaultValues?.[0];

            if (name !== props.field.name || !value) return;

            setValuesFromE164Number(value);
        };

        form.addEventListener(FORM_EVENTS.fieldDefaultValuesLoaded, onfieldDefaultValuesLoaded);

        return () => {
            form.removeEventListener(FORM_EVENTS.fieldDefaultValuesLoaded, onfieldDefaultValuesLoaded);
        };
    }, []);

    useEffect(() => {
        const form = e164InputRef.current?.closest('keap-form');

        if (!form) return;

        const onValidationFailed = ({ detail }) => {
            const fieldError = detail.errors?.find(([key]) => key === props.field.name);

            if (fieldError) {
                setShowError(true);
            }
        };

        const onFieldValueChanged = ({ detail }) => {
            if (detail.name !== props.field.name) return;

            const fieldErrors = detail.formState?.fields?.[props.field.name]?.errors ?? [];

            setShowError(fieldErrors.length > 0);
        };

        form.addEventListener(FORM_EVENTS.validationFailed, onValidationFailed);
        form.addEventListener(FORM_EVENTS.fieldValueChanged, onFieldValueChanged);

        return () => {
            form.removeEventListener(FORM_EVENTS.validationFailed, onValidationFailed);
            form.removeEventListener(FORM_EVENTS.fieldValueChanged, onFieldValueChanged);
        };
    }, []);

    useEffect(() => {
        const form = e164InputRef.current?.closest('keap-form');
        const fieldState = form?.formHandler?.formState?.fields?.[props.field.name];

        if (fieldState) {
            fieldState.isRequired = isRequired;
        }
    }, [isRequired]);

    const syncE164Value = (e164) => {
        const input = e164InputRef.current;
        const value = e164 ?? '';

        setE164Value(value);

        if (input) {
            input.value = value;
        }
    };

    const propagateE164Change = () => {
        if (!previousEmittedValue && !phoneNumber) return;

        const input = e164InputRef.current;

        if (input) {
            // Dispatch native change on blur so validation matches other text inputs (e.g. email).
            input.dispatchEvent(new Event('change', { bubbles: true }));
            setPreviousEmittedValue(e164Value);
        }
    };

    const handleCountryCodeChange = (nextCountryCode) => {
        const { formatted, e164 } = formatPhoneNumber(nextCountryCode, phoneNumber);

        setCountryCode(nextCountryCode);
        setIsOpen(false);
        setPhoneNumber(formatted);

        const val = isPhoneNumberEmpty ? '' : e164;

        syncE164Value(val);
    };

    const handlePhoneNumberChange = (e) => {
        const phoneNumber = e.target.value;
        const { formatted, e164 } = formatPhoneNumber(countryCode, phoneNumber);

        setPhoneNumber(formatted);
        syncE164Value(e164);
        setShowError(false);
    };

    const onDropdownToggle = () => {
        setIsOpen(!isOpen);
        setShowError(false);
    };

    const onInputBlur = () => {
        propagateE164Change();
    };

    return (
        <Stack gap={4}>
            <FormFieldContainer
                field={{
                    ...props.field,
                    required: isRequired,
                }}
                className={[props.className, isOpen ? styles.formFieldContainer : null].filter(Boolean).join(' ')}
                fieldType="intl-tel"
                formControl={
                    <div>
                        <input
                            ref={e164InputRef}
                            type="hidden"
                            name={props.field.name}
                            value={e164Value}
                        />

                        <div ref={dropdownRef} className={styles.inputContainer}>
                            <div className={styles.countrySelectorCell}>
                                <DropdownSelect
                                    selectedLabel={getSelectedLabel(countryCode)}
                                    placeholder={'Select Country'}
                                    isOpen={isOpen}
                                    onToggle={onDropdownToggle}
                                    className={styles.countrySelector}
                                />
                            </div>

                            <div data-testid="phone-field-column" className={styles.phoneFieldColumn}>
                                <input
                                    type="tel"
                                    name="phone_number"
                                    value={phoneNumber}
                                    onChange={handlePhoneNumberChange}
                                    placeholder="Enter your phone number"
                                    className={styles.phoneInput}
                                    required={isRequired}
                                    onBlur={onInputBlur}
                                />

                                <div className={['field-error-message', styles.errorMessage].join(' ')}>{errorMessage}</div>
                            </div>

                            {isOpen && <DropdownMenu
                                className={styles.countryDropdown}
                                options={options}
                                value={countryCode}
                                search={search}
                                onSearchChange={setSearch}
                                onSelect={handleCountryCodeChange}
                                onClose={closeDropdown}
                                renderOption={renderCountryOption}
                            />}
                        </div>
                    </div>
                }
                error={showError}
            />

            { showSmsOptInCheckbox && <div className={styles.smsOptInContainer}>
                <CheckboxField {...smsOptinProps} />
            </div> }
        </Stack>
    );
}

export default function InternationalPhoneInputContainer(props = {}) {
    return (
        <Hydratable id={INTL_PHONE_INPUT_ID} props={props}>
            <InternationalPhoneInput {...props} />
        </Hydratable>
    );
}
